import api from '@/services/api'
import type {
    BetaAdvertisement,
    BetaAdvertisementInput,
    BetaAdvertisementPosition,
    BetaAdvertisementTitles,
    BetaAccessCount,
    BetaAccessPeriod,
    BetaCreatePageInput,
    BetaActiveLayout,
    BetaDomainCheck,
    BetaDomain,
    BetaFile,
    BetaFileInput,
    BetaIntegrationCodes,
    BetaLinkBuildingLink,
    BetaLinkBuildingLinkInput,
    BetaLinkBuildingPage,
    BetaLayoutApplyResult,
    BetaLayoutPreview,
    BetaLayoutUndoResult,
    BetaOpportunity,
    BetaPage,
    BetaPost,
    BetaProfileIntegration,
    BetaProfileIntegrationProvider,
    BetaRedirect,
    BetaReport,
    BetaSiteAnalysis,
    BetaSocialLink,
    BetaSocialLinkInput,
    BetaSubscriptionBill,
    BetaSubscriptionContract,
    BetaSubscriptionsData,
    BetaTemplate,
} from '@/types/beta-dashboard'
import { hasPersistedMedia } from '@/utils/beta-readiness'

const parseResponse = async <T>(response: Response): Promise<T> => {
    const data = await response.json()

    if (!response.ok || data?.status === '0' || data?.status === 0 || data?.success === false) {
        throw new Error(data?.message || data?.error || 'Não foi possível concluir a solicitação.')
    }

    return data
}

export const finalizeBetaDashboardSession = async (siteId: number) => {
    const response = await api.get(`site/${siteId}/post/imported/update`)
    return parseResponse(response)
}

const advertisementPositions: BetaAdvertisementPosition[] = [
    'after_content',
    'footer',
    'sidebar',
    'menu',
]

const defaultAdvertisementTitles = (): BetaAdvertisementTitles => ({
    after_content: '',
    footer: '',
    sidebar: '',
    menu: '',
})

const normalizeAdvertisement = (item: Record<string, any>): BetaAdvertisement => ({
    id: Number(item.id),
    title: String(item.title || ''),
    position: advertisementPositions.includes(item.position)
        ? item.position
        : 'menu',
    advertisementType: ['text', 'banner', 'script'].includes(item.advertisementType)
        ? item.advertisementType
        : 'text',
    content: String(item.content || ''),
    image: String(item.image || ''),
    ref: String(item.ref || ''),
    urlImage: String(item.urlImage || ''),
    status: item.status === true || item.status === 1 || item.status === '1',
})

export const fetchBetaAdvertisements = async (siteId: number): Promise<{
    advertisements: BetaAdvertisement[]
    titles: BetaAdvertisementTitles
}> => {
    const response = await api.get(`site/${siteId}/flyer`)
    const data = await parseResponse<{
        data?: Array<Record<string, any>>
        titles?: Partial<BetaAdvertisementTitles>
    }>(response)

    return {
        advertisements: Array.isArray(data.data)
            ? data.data.map(normalizeAdvertisement)
            : [],
        titles: {
            ...defaultAdvertisementTitles(),
            ...(data.titles ?? {}),
        },
    }
}

export const createBetaAdvertisement = async (
    siteId: number,
    advertisement: BetaAdvertisementInput,
): Promise<BetaAdvertisement> => {
    const payload = {
        title: advertisement.title,
        position: advertisement.position,
        advertisementType: advertisement.advertisementType,
        content: advertisement.content,
        image: advertisement.image,
        ref: advertisement.ref,
    }
    const response = await api.post(
        `site/${siteId}/flyer/create`,
        JSON.stringify(payload),
    )
    const data = await parseResponse<{ data?: Record<string, any> }>(response)

    if (!data.data) throw new Error('A publicidade foi criada sem dados válidos.')

    return normalizeAdvertisement(data.data)
}

export const updateBetaAdvertisement = async (
    siteId: number,
    advertisement: BetaAdvertisement,
): Promise<BetaAdvertisement> => {
    const response = await api.post(
        `site/${siteId}/flyer/${advertisement.id}/update`,
        JSON.stringify(advertisement),
    )
    const data = await parseResponse<{ data?: Record<string, any> }>(response)

    return data.data ? normalizeAdvertisement(data.data) : advertisement
}

export const deleteBetaAdvertisement = async (siteId: number, advertisementId: number) => {
    const response = await api.post(`site/${siteId}/flyer/${advertisementId}/delete`, null)

    return parseResponse(response)
}

export const updateBetaAdvertisementTitles = async (
    siteId: number,
    titles: BetaAdvertisementTitles,
) => {
    const response = await api.post(
        `site/${siteId}/flyer/titles/update`,
        JSON.stringify(titles),
    )

    return parseResponse(response)
}

export const fetchBetaLinkBuilding = async (siteId: number): Promise<{
    page: BetaLinkBuildingPage
    links: BetaLinkBuildingLink[]
}> => {
    const response = await api.get(`site/${siteId}/linkBuilding`)
    const data = await parseResponse<{
        message?: {
            page?: BetaLinkBuildingPage
            linksBuilding?: BetaLinkBuildingLink[]
        }
    }>(response)

    const links = data.message?.linksBuilding
    const page = data.message?.page

    return {
        page: {
            logo: '',
            avatar: '',
            name: '',
            route: '',
            background_file: '',
            description: '',
            ...(page ?? {}),
            logo_type: String(page?.logo_type || '1'),
        },
        links: Array.isArray(links)
            ? links.map((link) => ({
                ...link,
                id: Number(link.id),
                route_name: String(link.route_name || ''),
                route_path: String(link.route_path || ''),
                type: Number(link.type || 7),
                target_blank: link.target_blank ? 1 : 0,
                typeRoute: link.typeRoute === 'whats' ? 'whats' : 'route',
                phone: String(link.phone || ''),
                ddi: link.ddi ?? 30,
            }))
            : [],
    }
}

export const updateBetaLinkBuildingPage = async (
    siteId: number,
    page: BetaLinkBuildingPage,
) => {
    const response = await api.post(
        `site/${siteId}/linkBuilding/updateForm`,
        JSON.stringify(page),
    )

    return parseResponse(response)
}

export const createBetaLinkBuildingLink = async (
    siteId: number,
    link: BetaLinkBuildingLinkInput,
): Promise<BetaLinkBuildingLink> => {
    const payload = {
        ...link,
        target_blank: link.target_blank ? 1 : 0,
    }
    const response = await api.post(
        `site/${siteId}/linkBuilding/create`,
        JSON.stringify(payload),
    )
    const data = await parseResponse<{ message?: { data?: BetaLinkBuildingLink } }>(response)

    if (!data.message?.data) throw new Error('O link foi criado sem dados válidos.')

    return {
        ...data.message.data,
        id: Number(data.message.data.id),
        route_name: String(data.message.data.route_name || ''),
        route_path: String(data.message.data.route_path || ''),
        type: Number(data.message.data.type || 7),
        target_blank: data.message.data.target_blank ? 1 : 0,
        typeRoute: data.message.data.typeRoute === 'whats' ? 'whats' : 'route',
        phone: String(data.message.data.phone || ''),
        ddi: data.message.data.ddi ?? 30,
    }
}

export const updateBetaLinkBuildingLink = async (
    siteId: number,
    link: BetaLinkBuildingLink,
): Promise<BetaLinkBuildingLink> => {
    const payload = {
        ...link,
        target_blank: link.target_blank ? 1 : 0,
    }
    const response = await api.post(
        `site/${siteId}/linkBuilding/${link.id}/update`,
        JSON.stringify(payload),
    )
    const data = await parseResponse<{ message?: { data?: BetaLinkBuildingLink } }>(response)

    return data.message?.data
        ? {
            ...data.message.data,
            id: Number(data.message.data.id),
            route_name: String(data.message.data.route_name || ''),
            route_path: String(data.message.data.route_path || ''),
            type: Number(data.message.data.type || 7),
            target_blank: data.message.data.target_blank ? 1 : 0,
            typeRoute: data.message.data.typeRoute === 'whats' ? 'whats' : 'route',
            phone: String(data.message.data.phone || ''),
            ddi: data.message.data.ddi ?? 30,
        }
        : payload
}

export const sortBetaLinkBuildingLinks = async (
    siteId: number,
    links: BetaLinkBuildingLink[],
) => {
    const response = await api.post(
        `site/${siteId}/linkBuilding/sort`,
        JSON.stringify({ linksBuilding: links }),
    )

    return parseResponse(response)
}

export const deleteBetaLinkBuildingLink = async (siteId: number, linkId: number) => {
    const response = await api.post(`site/${siteId}/linkBuilding/${linkId}/delete`, null)

    return parseResponse(response)
}

export const fetchBetaRedirects = async (siteId: number): Promise<BetaRedirect[]> => {
    const response = await api.get(`site/${siteId}/route/redirect`)
    const data = await parseResponse<{ data?: BetaRedirect[] }>(response)

    return Array.isArray(data.data) ? data.data : []
}

export const saveBetaRedirects = async (
    siteId: number,
    redirects: BetaRedirect[],
): Promise<BetaRedirect[]> => {
    const response = await api.post(
        `site/${siteId}/route/redirect/create`,
        JSON.stringify({ routes: redirects }),
    )
    const data = await parseResponse<{ data?: BetaRedirect[] }>(response)

    return Array.isArray(data.data) ? data.data : redirects
}

export const fetchBetaFiles = async (siteId: number): Promise<BetaFile[]> => {
    const response = await api.get(`site/${siteId}/file`)
    const data = await parseResponse<{ data?: BetaFile[] }>(response)

    return Array.isArray(data.data)
        ? data.data.map((file) => ({ ...file, id: Number(file.id) }))
        : []
}

export const uploadBetaFile = async (siteId: number, file: File): Promise<unknown> => {
    const formData = new FormData()
    formData.append('upload', file)

    const response = await api.file(`site/${siteId}/file/create`, formData)
    const data = response.data

    if (data?.status !== '1' && data?.status !== 1) {
        throw new Error(data?.message || 'Não foi possível enviar o arquivo.')
    }

    return data.data
}

export const createBetaFile = async (siteId: number, file: BetaFileInput) => {
    const response = await api.post(
        `site/${siteId}/file/create`,
        JSON.stringify(file),
    )

    return parseResponse(response)
}

export const deleteBetaFile = async (siteId: number, fileId: number) => {
    const response = await api.delete(`site/${siteId}/file/${fileId}/delete`)

    return parseResponse(response)
}

const normalizeProfileIntegration = (value: unknown): BetaProfileIntegration | null => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null

    const record = value as Record<string, any>
    const profile = record.data && typeof record.data === 'object'
        ? { ...record.data }
        : record

    return {
        ...profile,
        name: String(profile.name || profile.username || ''),
        picture: String(profile.picture || profile.avatar || ''),
        private: Boolean(profile.private),
    }
}

export const fetchBetaIntegrationCodes = async (siteId: number): Promise<BetaIntegrationCodes> => {
    const response = await api.get(`site/${siteId}/analytics`)
    const data = await parseResponse<{ data?: Partial<BetaIntegrationCodes> }>(response)

    return {
        pixelId: String(data.data?.pixelId || ''),
        analyticsId: String(data.data?.analyticsId || ''),
        analyticsIdV4: String(data.data?.analyticsIdV4 || ''),
        vimeoToken: String(data.data?.vimeoToken || ''),
    }
}

export const updateBetaIntegrationCodes = async (
    siteId: number,
    codes: BetaIntegrationCodes,
) => {
    const response = await api.post(
        `site/${siteId}/analytics/update`,
        JSON.stringify(codes),
    )

    return parseResponse(response)
}

export const fetchBetaSocialLinks = async (siteId: number): Promise<BetaSocialLink[]> => {
    const response = await api.get(`site/${siteId}/link`)
    const data = await parseResponse<{ data?: BetaSocialLink[] }>(response)

    return Array.isArray(data.data)
        ? data.data.map((link) => ({
            ...link,
            id: Number(link.id),
            api: Number(link.api),
            link: String(link.link || ''),
        }))
        : []
}

export const createBetaSocialLink = async (siteId: number, link: BetaSocialLinkInput) => {
    const response = await api.post(
        `site/${siteId}/link/create`,
        JSON.stringify(link),
    )

    return parseResponse(response)
}

export const updateBetaSocialLink = async (siteId: number, link: BetaSocialLinkInput) => {
    const response = await api.post(
        `site/${siteId}/link/${link.id}/update`,
        JSON.stringify(link),
    )

    return parseResponse(response)
}

export const deleteBetaSocialLink = async (siteId: number, linkId: number) => {
    const response = await api.post(`site/${siteId}/link/${linkId}/delete`, null)

    return parseResponse(response)
}

export const fetchBetaConnectedProfiles = async (
    siteId: number,
): Promise<Record<BetaProfileIntegrationProvider, BetaProfileIntegration | null>> => {
    const response = await api.get(`site/${siteId}/verify`)
    const data = await parseResponse<{
        apis_integration?: {
            Instagram?: { profile_data?: unknown }
            Youtube?: { profile_data?: unknown }
        }
    }>(response)

    return {
        insta: normalizeProfileIntegration(data.apis_integration?.Instagram?.profile_data),
        youtube: normalizeProfileIntegration(data.apis_integration?.Youtube?.profile_data),
    }
}

export const findBetaIntegrationProfile = async (
    siteId: number,
    provider: BetaProfileIntegrationProvider,
    username: string,
): Promise<BetaProfileIntegration> => {
    const response = await api.get(
        `site/${siteId}/profile/${provider}?username=${encodeURIComponent(username)}`,
    )
    const data = await parseResponse<{ content?: unknown }>(response)
    const profile = normalizeProfileIntegration(data.content)

    if (!profile) throw new Error('Não encontramos esse perfil.')

    return profile
}

export const connectBetaIntegrationProfile = async (
    siteId: number,
    provider: BetaProfileIntegrationProvider,
    username: string,
) => {
    const response = await api.post(
        `site/${siteId}/profile/create/${provider}?username=${encodeURIComponent(username)}`,
        null,
    )

    return parseResponse(response)
}

export const disconnectBetaIntegrationProfile = async (
    siteId: number,
    provider: BetaProfileIntegrationProvider,
) => {
    const response = await api.post(`site/${siteId}/disconnect/${provider}`, null)

    return parseResponse(response)
}

export const fetchBetaOpportunities = async (siteId: number): Promise<BetaOpportunity[]> => {
    const response = await api.get(`site/${siteId}/submit`)
    const data = await parseResponse<{ contents?: BetaOpportunity[] }>(response)

    return Array.isArray(data.contents)
        ? data.contents.map((opportunity) => ({
            ...opportunity,
            id: Number(opportunity.id),
            read: opportunity.read || null,
            created_at: opportunity.created_at || null,
            source: String(opportunity.source || ''),
            contents: Array.isArray(opportunity.contents) ? opportunity.contents : [],
        }))
        : []
}

export const fetchBetaOpportunitiesCount = async (
    siteId: number,
    days: BetaAccessPeriod = 30,
): Promise<number> => {
    const response = await api.get(`site/${siteId}/submit/count?d=${days}`)
    const data = await parseResponse<{ data?: { total?: number | string } }>(response)
    const total = Number(data.data?.total ?? 0)

    return Number.isFinite(total) && total > 0 ? Math.trunc(total) : 0
}

const normalizeSubscriptionContract = (
    contract: Record<string, any>,
): BetaSubscriptionContract => ({
    id: Number(contract.id),
    name: String(contract.name || ''),
    planName: String(contract.planName || ''),
    priceName: String(contract.priceName || ''),
    priceCicle: String(contract.priceCicle || ''),
    priceValue: contract.priceValue ?? '',
    payMethod: contract.payMethod ? String(contract.payMethod) : null,
    url: contract.url ? String(contract.url) : null,
    status: String(contract.status || ''),
})

const normalizeSubscriptionBill = (bill: Record<string, any>): BetaSubscriptionBill => ({
    id: Number(bill.id),
    status: String(bill.status || ''),
    product: String(bill.product || ''),
    expiry: String(bill.expiry || ''),
    value: bill.value ?? '',
    referer: String(bill.referer || ''),
    link: bill.link ? String(bill.link) : null,
    discount: bill.discount === undefined ? undefined : Number(bill.discount),
    observation: bill.observation ? String(bill.observation) : null,
    transactionDate: bill.transactionDate ? String(bill.transactionDate) : null,
    period: bill.period ? String(bill.period) : null,
})

export const fetchBetaPendingBills = async (siteId: number): Promise<BetaSubscriptionBill[]> => {
    const response = await api.get(`site/${siteId}/bill?status=waiting`)
    const data = await parseResponse<{ bill?: Array<Record<string, any>> }>(response)

    return Array.isArray(data.bill)
        ? data.bill.map(normalizeSubscriptionBill)
        : []
}

export const fetchBetaSubscriptions = async (siteId: number): Promise<BetaSubscriptionsData> => {
    const response = await api.get(`site/${siteId}/bill`)
    const data = await parseResponse<{
        contracts?: Array<Record<string, any>>
        bill?: Array<Record<string, any>>
        payMethod?: string | null
    }>(response)

    return {
        contracts: Array.isArray(data.contracts)
            ? data.contracts.map(normalizeSubscriptionContract)
            : [],
        bills: Array.isArray(data.bill)
            ? data.bill.map(normalizeSubscriptionBill)
            : [],
        payMethod: data.payMethod ? String(data.payMethod) : null,
    }
}

export const downloadBetaOpportunitiesCsv = async (siteId: number) => {
    const response = await api.get(`site/${siteId}/submit/csv`)

    if (!response.ok) throw new Error('Não foi possível exportar as oportunidades.')

    const disposition = response.headers.get('content-disposition') || ''
    const fileNameMatch = disposition.match(/filename="?([^";]+)"?/i)

    return {
        blob: await response.blob(),
        fileName: fileNameMatch?.[1] || `oportunidades-site-${siteId}.csv`,
    }
}

export const fetchBetaPages = async (siteId: number): Promise<BetaPage[]> => {
    const response = await api.get(`site/${siteId}/section`)
    const data = await parseResponse<{ content?: BetaPage[] }>(response)

    return Array.isArray(data.content) ? data.content : []
}

export const fetchBetaPosts = async (
    siteId: number,
    reference?: number,
): Promise<BetaPost[]> => {
    const suffix = reference ? `?reference=${reference}` : ''
    const response = await api.get(`site/${siteId}/dashboard/posts${suffix}`)
    const data = await parseResponse<{ data?: BetaPost[] }>(response)

    return Array.isArray(data.data) ? data.data : []
}

export const fetchBetaDomains = async (siteId: number): Promise<{
    domains: BetaDomain[]
    dnsTarget: string
}> => {
    const response = await api.get(`site/${siteId}/domain`)
    const data = await parseResponse<{ data?: BetaDomain[]; dns_target?: string }>(response)

    return {
        domains: Array.isArray(data.data) ? data.data : [],
        dnsTarget: data.dns_target ?? '',
    }
}

export const fetchBetaDomainStatus = async (
    siteId: number,
    domainId: number,
): Promise<BetaDomainCheck> => {
    const response = await api.get(`site/${siteId}/domain/${domainId}/dns`)
    const data = await parseResponse<{
        data?: 'online' | 'loading' | 'offline'
        dns_configured?: boolean
        https_ready?: boolean
        target?: string
        checked_at?: string
    }>(response)

    const dnsConfigured = data.dns_configured === true
    const httpsReady = data.https_ready === true
    const status = httpsReady
        ? 'online'
        : dnsConfigured
            ? 'provisioning'
            : data.data === 'offline'
                ? 'offline'
                : 'loading'

    return {
        status,
        dnsConfigured,
        httpsReady,
        target: data.target ?? '',
        checkedAt: data.checked_at,
    }
}

export const fetchBetaReport = async (siteId: number): Promise<BetaReport | null> => {
    const response = await api.get(`site/${siteId}/report?d=30`)
    const data = await parseResponse<{ data?: BetaReport }>(response)

    return data.data ?? null
}

export const fetchBetaAccessCount = async (
    siteId: number,
    days: BetaAccessPeriod = 30,
    page = 1,
): Promise<BetaAccessCount> => {
    const response = await api.get(`site/${siteId}/access-count?d=${days}&page=${page}`)
    const data = await parseResponse<{
        data?: {
            total?: unknown
            days?: unknown
            from?: unknown
            to?: unknown
            pages?: Array<Record<string, unknown>>
            pagination?: {
                page?: unknown
                per_page?: unknown
                total?: unknown
                last_page?: unknown
            }
        }
    }>(response)
    const payload = data.data
    const rawPages = payload?.pages ?? []
    const responsePages = Array.isArray(rawPages) ? rawPages : []
    const pagination = payload?.pagination

    return {
        total: Number(payload?.total ?? 0),
        days: [1, 15, 30].includes(Number(payload?.days))
            ? Number(payload?.days) as BetaAccessPeriod
            : days,
        from: String(payload?.from ?? ''),
        to: String(payload?.to ?? ''),
        pages: responsePages.map((item: any) => ({
            pageId: Number(item?.page_id ?? 0),
            title: String(item?.title ?? ''),
            accessCount: Number(item?.access_count ?? 0),
        })),
        pagination: {
            page: Number(pagination?.page ?? page),
            perPage: Number(pagination?.per_page ?? 20),
            total: Number(pagination?.total ?? responsePages.length),
            lastPage: Number(pagination?.last_page ?? 1),
        },
    }
}

export const fetchBetaPageDocument = async (siteId: number, pageId: number) => {
    const response = await api.get(`site/${siteId}/post/${pageId}/edit`)
    const data = await parseResponse<{ data?: unknown }>(response)

    return data.data ?? null
}

export const sortBetaPages = async (siteId: number, pages: BetaPage[]): Promise<void> => {
    const response = await api.post(
        `site/${siteId}/category/update`,
        JSON.stringify({ categories: pages }),
    )
    await parseResponse(response)
}

export const createBetaPage = async (
    siteId: number,
    pages: BetaPage[],
    page: BetaCreatePageInput,
): Promise<{ page: BetaPage; pages: BetaPage[] }> => {
    const combination = page.combination
        .map((item: any) => item?.id ?? item)
        .filter((item) => item !== null && item !== undefined)

    const categories = [
        ...pages,
        {
            name: page.name,
            target: page.target,
            id: null,
            visible: page.type === 'link' ? 0 : 1,
            type: page.type,
            options: page.options,
            combination,
        },
    ]

    const response = await api.post(
        `site/${siteId}/category/update`,
        JSON.stringify({ categories }),
    )
    const data = await parseResponse<{ content?: BetaPage[] }>(response)
    const updatedPages = Array.isArray(data.content) ? data.content : []
    const matches = updatedPages.filter((item) => item.name === page.name)
    const createdPage = matches[matches.length - 1]

    if (!createdPage) {
        throw new Error('A página foi criada, mas não foi possível identificá-la na resposta.')
    }

    return {
        page: createdPage,
        pages: updatedPages,
    }
}

export const createBetaPost = async (siteId: number, reference: number): Promise<number> => {
    const response = await api.post(
        `site/${siteId}/post/new`,
        JSON.stringify({ id: reference }),
    )
    const data = await parseResponse<{ postId?: number }>(response)

    if (!data.postId) {
        throw new Error('O post foi criado sem um identificador válido.')
    }

    return data.postId
}

export const updateBetaPage = async (siteId: number, page: BetaPage) => {
    const response = await api.post(
        `site/${siteId}/category/${page.id}/update`,
        JSON.stringify(page),
    )

    return parseResponse(response)
}

export const duplicateBetaPage = async (siteId: number, pageId: number) => {
    const response = await api.post(`site/${siteId}/post/${pageId}/duplicate`, null)

    return parseResponse(response)
}

export const deleteBetaPage = async (siteId: number, pageId: number) => {
    const response = await api.post(`site/${siteId}/category/${pageId}/1/delete`, null)

    return parseResponse(response)
}

export const createBetaDomain = async (siteId: number, domain: string) => {
    const response = await api.post(
        `site/${siteId}/domain/create`,
        JSON.stringify({ url: domain, main: 0 }),
    )

    return parseResponse(response)
}

export const setBetaMainDomain = async (siteId: number, domainId: number) => {
    const response = await api.post(
        `site/${siteId}/domain/${domainId}/main`,
        JSON.stringify({ domain: domainId }),
    )

    return parseResponse(response)
}

export const deleteBetaDomain = async (siteId: number, domainId: number) => {
    const response = await api.post(`site/${siteId}/domain/${domainId}/delete`, null)

    return parseResponse(response)
}

export const openBetaStore = async (siteId: number) => {
    const response = await api.get(`store/login?site=${siteId}`)
    const data = await parseResponse<{ url?: string }>(response)

    if (!data.url) {
        throw new Error('A integração da loja não devolveu uma URL de acesso.')
    }

    return data.url
}

export const fetchBetaTemplates = async (): Promise<BetaTemplate[]> => {
    const response = await api.get('template')
    const data = await parseResponse<{ content?: BetaTemplate[] }>(response)

    return Array.isArray(data.content)
        ? data.content.filter((template) => (
            Number.isFinite(Number(template.id))
            && Number(template.id) >= 10
            && Boolean(template.thumbnail)
            && Boolean(template.url)
            && template.dataDecoded
            && typeof template.dataDecoded === 'object'
        ))
        : []
}

export const fetchBetaActiveLayout = async (siteId: number): Promise<BetaActiveLayout> => {
    const response = await api.get(`site/${siteId}/layout`)
    const data = await parseResponse<Partial<BetaActiveLayout>>(response)

    if (!data.layoutId || !data.templateId || !data.data || typeof data.data !== 'object') {
        throw new Error('O layout atual não pôde ser identificado.')
    }

    return {
        previewAvailable: data.previewAvailable === true,
        layoutId: Number(data.layoutId),
        templateId: Number(data.templateId),
        templateName: String(data.templateName || 'Layout atual'),
        layoutHash: data.layoutHash ? String(data.layoutHash) : undefined,
        data: data.data,
    }
}

export const createBetaLayoutPreview = async (siteId: number, templateId: number, signal?: AbortSignal): Promise<BetaLayoutPreview> => {
    const response = await api.post(`site/${siteId}/beta/layout/preview`, JSON.stringify({ templateId }), 'application/json', signal)
    const result = await parseResponse<{ data?: BetaLayoutPreview }>(response)
    const preview = result.data
    if (!preview || typeof preview.token !== 'string' || !preview.token || !Number.isFinite(Date.parse(preview.expiresAt))) {
        throw new Error('A prévia retornou uma resposta inválida.')
    }
    const url = new URL(preview.previewUrl)
    if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password || url.searchParams.get('layoutPreview') !== preview.token) {
        throw new Error('O endereço da prévia é inválido.')
    }
    return preview
}

export const applyBetaTemplate = async (
    siteId: number,
    templateId: number,
    previewToken?: string,
): Promise<BetaLayoutApplyResult> => {
    const response = await api.post(
        `site/${siteId}/beta/layout/apply`,
        JSON.stringify({ templateId, ...(previewToken ? { previewToken } : {}) }),
    )
    const data = await parseResponse<{ data?: BetaLayoutApplyResult }>(response)

    if (!data.data?.layoutId || !data.data.rollback?.token) {
        throw new Error('A troca foi concluída sem um token válido para desfazer.')
    }

    return data.data
}

export const undoBetaTemplate = async (
    siteId: number,
    rollbackToken: string,
): Promise<BetaLayoutUndoResult> => {
    const response = await api.post(
        `site/${siteId}/beta/layout/undo`,
        JSON.stringify({ rollbackToken }),
    )
    const data = await parseResponse<{ data?: BetaLayoutUndoResult }>(response)

    if (!data.data?.layoutId || !data.data.templateId) {
        throw new Error('O layout anterior foi restaurado sem uma resposta válida.')
    }

    return data.data
}

export const fetchBetaSiteAnalysis = async (
    siteId: number,
    refresh = false,
): Promise<BetaSiteAnalysis> => {
    const response = await api.post(
        `site/${siteId}/beta/analysis`,
        JSON.stringify({ refresh }),
    )
    const data = await parseResponse<{ data?: BetaSiteAnalysis }>(response)

    if (!data.data || !Array.isArray(data.data.suggestions)) {
        throw new Error('A análise não devolveu um resultado válido.')
    }

    return data.data
}

export const hasPersistedGalleryMedia = hasPersistedMedia
